import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, TextInput, PasswordInput, Modal } from "@mantine/core";
import apiService from "../../services/api";
import { showAlert } from "../../utils/notify";
import PetSetup from "../client/PetSetup";
import ClientPortal from "../client/ClientPortal";
import TrainerSetup from "../trainer/TrainerSetup";
import type { LoginPageProps, User } from "../../types";

const COMING_SOON = process.env.REACT_APP_COMING_SOON === "true";

interface LoginFormData {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  userType: string;
  username: string;
}

type FlowType = "auth" | "petSetup" | "trainerSetup" | "trainerDisclaimer" | "clientPortal";
type VerificationStep = "none" | "requested" | "verified" | "skipped";

const LoginPage: React.FC<LoginPageProps> = ({ onBackToHome, onLoginSuccess }) => {
  const [searchParams] = useSearchParams();
  const trainerOnly = COMING_SOON || searchParams.get("trainerOnly") === "1";
  const [isLogin, setIsLogin] = useState<boolean>(true);
  const [currentFlow, setCurrentFlow] = useState<FlowType>("auth"); // 'auth', 'petSetup', 'trainerSetup', 'trainerDisclaimer', 'clientPortal'
  const [userData, setUserData] = useState<Record<string, unknown> | null>(null);
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    userType: trainerOnly ? "trainer" : "",
    username: "", // Kept for registration API compatibility
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [resetEmail, setResetEmail] = useState<string>("");
  const [resetSuccess, setResetSuccess] = useState<boolean>(false);
  const [verificationStep, setVerificationStep] = useState<VerificationStep>("none"); // 'none', 'requested', 'verified'
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [verificationToken, setVerificationToken] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePetSetupComplete = () => {
    // Store client ID for the portal
    localStorage.setItem("clientId", userData?.user_id as string);
    // Redirect to client portal
    onLoginSuccess("client");
  };

  const handleTrainerSetupComplete = () => {
    localStorage.setItem("trainerProfileCompleted", "true");
    // Redirect to trainer portal
    onLoginSuccess("trainer");
  };

  const handleLogout = () => {
    setCurrentFlow("auth");
    setUserData(null);
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      name: "",
      userType: trainerOnly ? "trainer" : "",
      username: "",
    });
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 10) {
      errors.push("Password must be at least 10 characters long");
    }

    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }

    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push("Password must contain at least one special character");
    }

    return errors;
  };

  const validateForm = (): boolean => {
    if (!formData.email || !formData.password) {
      setError("Email and password are required");
      return false;
    }

    if (!isLogin) {
      if (!formData.email) {
        setError("Email is required");
        return false;
      }
      if (!formData.userType) {
        setError("Please select whether you are a client or trainer");
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        return false;
      }

      // Validate password complexity
      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        setError(passwordErrors.join(". "));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("=== FORM SUBMITTED ===");
    console.log("Form data:", formData);
    console.log("Is login:", isLogin);

    if (!validateForm()) {
      console.log("Form validation failed");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isLogin) {
        console.log("Attempting login...");
        // Login - use email as username for backend compatibility
        const response = (await apiService.login(formData.email, formData.password)) as Record<
          string,
          unknown
        >;

        // Store token for future use
        localStorage.setItem("token", response.access_token as string);
        localStorage.setItem("user_id", response.user_id as string);
        localStorage.setItem("username", response.username as string);

        // Store user data for potential profile completion
        setUserData({
          user_id: response.user_id,
          username: response.username,
          user_type: response.user_type,
          email: response.email,
        });

        // Store client/trainer ID based on user type
        console.log("=== LOGIN RESPONSE ===");
        console.log("Response:", response);
        console.log("User type:", response.user_type);
        console.log("User ID:", response.user_id);

        // Store email for later use
        localStorage.setItem("email", (response.email as string) || formData.email);

        if (response.user_type === "client") {
          console.log("Setting clientId to:", response.user_id);
          localStorage.setItem("clientId", response.user_id as string);
        } else if (response.user_type === "trainer") {
          // For trainers, we need to get the actual trainer ID from the trainer database
          try {
            const trainerResponse = (await apiService.getTrainerID(
              response.email as string,
            )) as Record<string, unknown>;
            if (trainerResponse.Code === 200) {
              localStorage.setItem("trainerId", trainerResponse.id as string);
            } else {
              console.error("Failed to get trainer ID:", trainerResponse.Message);
              // Fallback to user_id if trainer ID not found
              localStorage.setItem("trainerId", response.user_id as string);
            }
          } catch (err) {
            console.error("Error getting trainer ID:", err);
            // Fallback to user_id if error occurs
            localStorage.setItem("trainerId", response.user_id as string);
          }
          const trainerSetupDone =
            !!response.trainer_profile_exists &&
            (!!response.background_check_completed || !!response.payment_completed);
          localStorage.setItem("trainerProfileCompleted", String(trainerSetupDone));
        }

        // Redirect based on user status and profile completion.
        // Trainer finishes "setup" once the profile exists and they have either paid
        // the registration fee or been fully approved by admin — pending-review trainers
        // still land in the portal, not the setup flow.
        const trainerSetupDone =
          !!response.trainer_profile_exists &&
          (!!response.background_check_completed || !!response.payment_completed);
        if (!response.user_type) {
          // User needs to select type - redirect to trainer selection for now
          // In a full implementation, you'd redirect to user type selection
          onLoginSuccess("client");
        } else if (response.user_type === "trainer" && !trainerSetupDone) {
          // Trainer needs to complete profile setup
          onLoginSuccess("trainer-setup");
        } else {
          // User is ready - redirect to appropriate portal
          onLoginSuccess(response.user_type as string);
        }
      } else {
        // Register - check if email verification is required
        if (verificationStep === "none") {
          // Step 1: Check if email verification is required
          setLoading(true);
          setError(null);
          try {
            const verificationResponse = (await apiService.requestEmailVerification(
              formData.email,
              formData.userType,
            )) as Record<string, unknown>;

            // Check if verification is required
            if (verificationResponse.verification_required === false) {
              // Email verification not required - proceed directly to registration
              setVerificationStep("skipped");
              // Continue to registration without token
              const response = (await apiService.register(
                formData.email,
                formData.email,
                formData.password,
                formData.name,
                formData.userType,
                "", // No verification token needed
              )) as Record<string, unknown>;

              // Store user data
              setUserData({
                user_id: response.user_id,
                username: response.username,
                user_type: response.user_type,
                email: formData.email,
              });

              setError(null);

              // Redirect based on user type
              if (response.user_type === "client") {
                setCurrentFlow("petSetup");
              } else if (response.user_type === "trainer") {
                setCurrentFlow("trainerDisclaimer");
              } else {
                onLoginSuccess(response.user_type as string);
              }
              setLoading(false);
              return;
            } else {
              // Email verification is required
              setVerificationStep("requested");
              setError(null);
            }
          } catch (err: unknown) {
            console.error("Email verification request error:", err);
            setError(
              (err as Error).message || "Failed to send verification code. Please try again.",
            );
          } finally {
            setLoading(false);
          }
          return;
        } else if (verificationStep === "requested") {
          // Step 2: Verify the code
          if (!verificationCode || verificationCode.length !== 6) {
            setError("Please enter the 6-digit verification code");
            return;
          }
          setLoading(true);
          setError(null);
          try {
            const verifyResponse = (await apiService.verifyEmailCode(
              formData.email,
              verificationCode,
            )) as Record<string, unknown>;
            if (verifyResponse.verification_token) {
              setVerificationToken(verifyResponse.verification_token as string);
              setVerificationStep("verified");
              setError(null);
            } else {
              setError("Failed to verify code. Please try again.");
            }
          } catch (err: unknown) {
            console.error("Email verification error:", err);
            setError((err as Error).message || "Invalid verification code. Please try again.");
          } finally {
            setLoading(false);
          }
          return;
        } else if (verificationStep === "verified" && verificationToken) {
          // Step 3: Complete registration with verification token
          console.log("Registration data:", {
            username: formData.email,
            email: formData.email,
            password: formData.password,
            name: formData.name,
            userType: formData.userType,
            verificationToken: verificationToken,
          });

          const response = (await apiService.register(
            formData.email,
            formData.email,
            formData.password,
            formData.name,
            formData.userType,
            verificationToken,
          )) as Record<string, unknown>;

          // Store user data
          setUserData({
            user_id: response.user_id,
            username: response.username,
            user_type: response.user_type,
            email: formData.email,
          });

          setError(null);

          // Redirect based on user type
          if (response.user_type === "client") {
            setCurrentFlow("petSetup");
          } else if (response.user_type === "trainer") {
            setCurrentFlow("trainerDisclaimer");
          } else {
            // For other types, go directly to selection
            onLoginSuccess(response.user_type as string);
          }
        }
      }
    } catch (err: unknown) {
      console.error("Auth error:", err);
      setError((err as Error).message || (isLogin ? "Login failed" : "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!resetEmail) {
      setError("Please enter your email address");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiService.forgotPassword(resetEmail);

      setResetSuccess(true);
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetEmail("");
        setResetSuccess(false);
      }, 3000);
    } catch (err: unknown) {
      console.error("Password reset error:", err);
      setError("Failed to send password reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle different flows
  if (currentFlow === "petSetup") {
    return (
      <PetSetup
        onComplete={handlePetSetupComplete}
        onBackToHome={onBackToHome}
        clientId={userData?.user_id as string}
      />
    );
  }

  if (currentFlow === "trainerDisclaimer") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-5 bg-background font-sans">
        <div className="bg-card rounded-xl p-5 sm:p-8 md:p-10 shadow-lg border border-border max-w-[400px] mx-4 sm:mx-auto w-full">
          <button
            onClick={onBackToHome}
            className="bg-transparent text-muted-foreground hover:text-foreground border-none cursor-pointer text-sm mb-5 underline transition-colors"
          >
            ← Back to Home
          </button>

          <div className="text-3xl sm:text-5xl mb-5 text-center">🐕‍🦺</div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-foreground text-center">
            Trainer Registration Requirements
          </h1>

          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-5 mb-8 text-left">
            <p className="text-base text-yellow-800 leading-relaxed mb-4">
              Before beginning the sign up process, please note the following. You can build your account with information (filters, bio, resume, etc), and can log out and edit as often as you want. However, you will be required to provide liability insurance that covers Pheefee Technologies Inc, pass a background check, and pay a one time registration fee of <strong>$50</strong>.
            </p>
          </div>

          <Button onClick={() => setCurrentFlow("trainerSetup")} fullWidth radius="md" size="lg">
            I Understand, Continue
          </Button>
        </div>
      </div>
    );
  }

  if (currentFlow === "trainerSetup") {
    return (
      <TrainerSetup
        onSetupComplete={handleTrainerSetupComplete}
        onBackToHome={onBackToHome}
        userData={userData as User | null}
      />
    );
  }

  if (currentFlow === "clientPortal") {
    return (
      <ClientPortal
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        isLoggedIn={true}
        onFindTrainer={() => {}}
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-5 bg-background font-sans">
      <div className="bg-card rounded-xl p-5 sm:p-8 md:p-10 shadow-lg border border-border max-w-[400px] mx-4 sm:mx-auto w-full">
        <button
          onClick={onBackToHome}
          className="bg-transparent text-muted-foreground hover:text-foreground border-none cursor-pointer text-sm mb-5 underline transition-colors"
        >
          ← Back to Home
        </button>

        <div className="text-3xl sm:text-5xl mb-5 text-center">🐕‍🦺</div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-foreground text-center">
          Dog Training On Demand
        </h1>
        <p className="text-base text-muted-foreground mb-8 text-center">
          {isLogin ? "Welcome back!" : "Join our community"}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <TextInput
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleInputChange({
                ...e,
                target: { ...e.target, name: "email", value: e.target.value },
              })
            }
            required
            radius="md"
            size="md"
          />

          {!isLogin && (
            <>
              <TextInput
                placeholder="Full Name (optional)"
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange({
                    ...e,
                    target: { ...e.target, name: "name", value: e.target.value },
                  })
                }
                radius="md"
                size="md"
              />

              {!trainerOnly && (
              <div className="mb-5">
                <label className="block font-semibold text-foreground mb-2 text-base">
                  I am a: *
                </label>
                <div className="flex flex-col sm:flex-row gap-4">
                  <label
                    className={`flex items-center gap-2 cursor-pointer py-2.5 px-4 border-2 rounded-lg flex-1 justify-center transition ${
                      formData.userType === "client"
                        ? "border-primary bg-primary-subtle"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="userType"
                      value="client"
                      checked={formData.userType === "client"}
                      onChange={handleInputChange}
                      className="m-0 accent-primary"
                    />
                    <span>🐕 Client (Need Training)</span>
                  </label>

                  <label
                    className={`flex items-center gap-2 cursor-pointer py-2.5 px-4 border-2 rounded-lg flex-1 justify-center transition ${
                      formData.userType === "trainer"
                        ? "border-primary bg-primary-subtle"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="userType"
                      value="trainer"
                      checked={formData.userType === "trainer"}
                      onChange={handleInputChange}
                      className="m-0 accent-primary"
                    />
                    <span>🎓 Trainer (Provide Training)</span>
                  </label>
                </div>
              </div>
              )}
            </>
          )}

          <PasswordInput
            placeholder="Password"
            value={formData.password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleInputChange({
                ...e,
                target: { ...e.target, name: "password", value: e.target.value },
              })
            }
            required
            radius="md"
            size="md"
          />

          {!isLogin && (
            <>
              <PasswordInput
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange({
                    ...e,
                    target: { ...e.target, name: "confirmPassword", value: e.target.value },
                  })
                }
                required
                radius="md"
                size="md"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Password must be at least 10 characters with uppercase, lowercase, and special
                character
              </p>

              {/* Email Verification Step */}
              {verificationStep === "requested" && (
                <div className="bg-success/10 border border-success/30 rounded-lg p-4 mt-4">
                  <p className="text-sm text-success mb-2 text-center">
                    We've sent a verification code to <strong>{formData.email}</strong>
                  </p>
                  <TextInput
                    placeholder="Enter 6-digit code"
                    value={verificationCode}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setVerificationCode(value);
                    }}
                    maxLength={6}
                    required
                    radius="md"
                    size="md"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      setLoading(true);
                      setError(null);
                      try {
                        await apiService.requestEmailVerification(
                          formData.email,
                          formData.userType,
                        );
                        setError(null);
                        showAlert("Verification code resent!");
                      } catch (err: unknown) {
                        setError(
                          (err as Error).message || "Failed to resend code. Please try again.",
                        );
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="bg-transparent text-success border border-success/40 hover:bg-success/10 rounded-md py-2 px-4 text-sm cursor-pointer mt-2 w-full transition"
                  >
                    Resend Code
                  </button>
                </div>
              )}
            </>
          )}

          <Button type="submit" loading={loading} radius="md" size="lg" className="mt-2">
            {isLogin ? "Login" : "Sign up"}
          </Button>
        </form>

        {isLogin && (
          <button
            onClick={() => setShowForgotPassword(true)}
            className="bg-transparent text-muted-foreground hover:text-foreground border-none cursor-pointer text-sm text-center mt-2 mb-2 w-full transition-colors"
          >
            Forgot your password?
          </button>
        )}

        <button
          onClick={() => {
            setIsLogin(!isLogin);
            setError(null);
            setVerificationStep("none");
            setVerificationCode("");
            setVerificationToken(null);
            setFormData({
              email: "",
              password: "",
              confirmPassword: "",
              name: "",
              userType: trainerOnly ? "trainer" : "",
              username: "",
            });
          }}
          className="bg-transparent text-primary hover:text-primary-hover border-none cursor-pointer text-sm text-center mt-2 underline w-full transition-colors"
        >
          {isLogin ? "Don't have an account? Sign Up" : "Already have an account? Login"}
        </button>

        {error && <div className="text-destructive text-sm mt-3 text-center">{error}</div>}
        {loading && <div className="text-primary text-sm mt-3 text-center">Processing...</div>}
      </div>

      {/* Forgot Password Modal */}
      <Modal
        opened={showForgotPassword}
        onClose={() => {
          setShowForgotPassword(false);
          setResetEmail("");
          setError(null);
        }}
        title="Reset Password"
        centered
        radius="md"
        size="md"
      >
        {resetSuccess ? (
          <div className="p-5 sm:p-8 bg-success/10 rounded-lg border border-success/30 mt-4">
            <p className="text-success text-base m-0">
              ✓ Password reset email sent! Check your inbox for instructions.
            </p>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="flex flex-col gap-4">
            <p className="text-muted-foreground text-base mb-3">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
            <TextInput
              type="email"
              placeholder="Enter your email"
              value={resetEmail}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResetEmail(e.target.value)}
              required
              radius="md"
              size="md"
            />
            <div className="flex gap-3 mt-5 justify-end">
              <Button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetEmail("");
                  setError(null);
                }}
                variant="outline"
                color="gray"
                radius="md"
              >
                Cancel
              </Button>
              <Button type="submit" loading={loading} radius="md">
                Send Reset Link
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default LoginPage;
